import * as vp from 'vopay';

import { FunctionComponent, useEffect, useState, useCallback, useContext } from 'react';
import { useParams } from 'react-router-dom';
import AccountContext from '../AccountContext';
import Details from '../Details';
import Permissions from '../Permissions/PermissionsContainer';
import Billing from '../Billing';
import Users from '../Users/UsersContainer';
import TransactionsContainer from '../Transactions/TransactionsContainer';
import AgreementContainer from '../Agreement/AgreementContainer';
import SignersContainer from '../Signers/SignersContainer';

import useStorage from '../../../hooks/useStorage';
import { usePartnerLogin } from '../../../providers/PartnerLogin.provider';
import { useModuleFeatureFlag } from '../../../providers/FeatureFlag.provider';
import useFetchAgreementDetails from '../../../hooks/useApi/useFetchAgreementDetails';
import useFetchSigningAuthorityInfo from '../../../hooks/useApi/useFetchSigningAuthorityInfo';

import type { AccountsHandlers } from '../types';

const { Box, Tabs } = vp;

type AccountProps = {
  transactions: vp.AccountTransactionsMap;
  handlers: AccountsHandlers;
};

type TabKey =
  | 'agreement'
  | 'balances'
  | 'billing'
  | 'details'
  | 'permissions'
  | 'signers'
  | 'subaccounts'
  | 'transactions'
  | 'users';
type FC = FunctionComponent<AccountProps>;

const AccountTabs: FC = ({ transactions, handlers }: AccountProps) => {
  const { details } = useContext(AccountContext);
  const [activeTab, setActiveTab] = useState<TabKey>('details');
  const [index, setIndex] = useState(0);
  const { canAccessBillingTab } = useModuleFeatureFlag('accounts');
  const {
    response: { isInsideSales },
  } = usePartnerLogin();

  const {
    signerCreation: { getSignerCreation, clearSignerCreation },
    accountToken: { storeAccountToken },
  } = useStorage();

  const storedSignerCreation = getSignerCreation();
  const { accountId } = useParams<{ accountId: string }>();
  const accountToken = details[accountId].token;
  const [agreementDetails, fetchAgreementDetails] = useFetchAgreementDetails({ bearer: accountToken });
  const [signingAuthorityInfo, fetchSigningAuthorityInfo] = useFetchSigningAuthorityInfo({
    bearer: accountToken || '',
  });
  const [isAddSignerToastVisible, setIsAddSignerToastVisible] = useState(false);

  useEffect(() => {
    storeAccountToken(accountToken);
  }, [details, accountId, accountToken]);

  useEffect(() => {
    fetchAgreementDetails();
  }, []);

  useEffect(() => {
    fetchSigningAuthorityInfo();
  }, [accountId, accountToken]);

  useEffect(() => {
    if (storedSignerCreation === 'signerToast') {
      fetchSigningAuthorityInfo();
      setActiveTab('signers');
      setTimeout(() => {
        clearSignerCreation();
      }, 200);
      setIsAddSignerToastVisible(true);
      setTimeout(() => {
        setIsAddSignerToastVisible(false);
      }, 2000);
    } else if (storedSignerCreation === 'signerChange') {
      fetchSigningAuthorityInfo();
      setActiveTab('signers');
      setTimeout(() => {
        clearSignerCreation();
      }, 200);
    }
  }, [storedSignerCreation]);

  const handleAgreementTab = () => {
    setActiveTab('agreement');
  };

  const handleSignersTab = () => {
    setActiveTab('signers');
  };

  const handleSignerLists = () => {
    fetchSigningAuthorityInfo();
  };

  const getTabs = useCallback(() => {
    const tabs = [
      {
        label: 'Details',
        showField: true,
        onClick: () => setActiveTab('details'),
        key: '0',
      },
      {
        label: 'Permissions',
        showField: true,
        onClick: () => setActiveTab('permissions'),
        key: '1',
      },
      {
        label: 'Transactions',
        showField: true,
        onClick: () => setActiveTab('transactions'),
        key: '2',
      },
      {
        label: 'Billing',
        showField: true,
        filter: () => canAccessBillingTab,
        onClick: () => setActiveTab('billing'),
        key: '3',
      },
      {
        label: 'Portal',
        showField: true,
        onClick: () => setActiveTab('users'),
        key: '4',
      },
    ];

    if (isInsideSales) {
      return tabs.filter((data) => data.showField);
    }
    return tabs.filter((data) => data.showField);
  }, [isInsideSales]);

  useEffect(() => {
    const idx = getTabs().findIndex(({ key }) => key === activeTab);
    setIndex(idx === -1 ? 0 : idx);
  }, [activeTab]);

  return (
    <div>
      <Box mb="lg" flx="col">
        <Box mb="lg" flx="col">
          <Tabs variant="dynamic" index={index} tabs={getTabs()} />
        </Box>
      </Box>

      {activeTab === 'details' && (
        <Details details={details} agreementDetails={agreementDetails} onClick={handleAgreementTab} />
      )}
      {activeTab === 'permissions' && <Permissions handlers={handlers} />}
      {activeTab === 'transactions' && (
        <TransactionsContainer defaultTransactions={transactions} details={details} handlers={handlers} />
      )}
      {activeTab === 'agreement' && (
        <AgreementContainer
          agreementDetails={agreementDetails}
          accountToken={accountToken}
          signingAuthorityInfo={signingAuthorityInfo}
          onClick={handleSignersTab}
        />
      )}
      {activeTab === 'signers' && (
        <SignersContainer
          agreementDetails={agreementDetails}
          accountToken={accountToken}
          isAddSignerToastVisible={isAddSignerToastVisible}
          signingAuthorityInfo={signingAuthorityInfo}
          handleSignerLists={handleSignerLists}
        />
      )}
      {activeTab === 'billing' && <Billing />}
      {activeTab === 'users' && <Users details={details} />}
    </div>
  );
};

export default AccountTabs;
